import {
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useApolloClient } from '@apollo/client';
import snakeCase from 'lodash/snakeCase';
import { generatePath, useHistory, useRouteMatch } from 'react-router-dom';
import queryString from 'query-string';

import {
  FILTER_QUERIES,
  MyClothesSortTypes,
} from '@constants';
import {
  useProductCharacteristicsFilterQuery,
  ProductCharacteristicsFilterQuery,
} from '@containers/ProductCharacteristicsFilter/gql/graphql.v3.generated';
import { useCatalogRouteMatch, useIsClient, useSearchParams } from '@utils/common';
import { indexedFilter } from '@urls';

import {
  useProductFeedFiltersQuery,
  ProductFeedFiltersQuery,
  BrandFilterDataFragment,
  BrandFilterDataFragmentDoc,
} from './graphql.v5.generated';
import {
  useFeedCityQuery,
  FeedCityQuery,
} from './graphql.v3.generated';


export type ActiveFilters = {
  priceFrom?: number;
  priceTo?: number;
  prices: number[];
  catalogSlug?: string;
  orderBy?: MyClothesSortTypes;
  sizes: number[];
  conditions: number[];
  characteristics: number[];
  brands: number[];
  colors: number[];
  searchText?: string;
  ukrainian?: string;
  freeShipping?: boolean;
  isOnSale?: boolean;
  cities: number[];
};

// Omit all non snake_case params and replace them with snake_case
export type FiltersSearchParams = Omit<
ActiveFilters,
'catalogSlug'
| 'orderBy'
| 'priceFrom'
| 'priceTo'
| 'searchText'
| 'freeShipping'
| 'isOnSale'
> & {
  catalog: string,
  sort: MyClothesSortTypes,
  price_from: ActiveFilters['priceFrom'],
  price_to: ActiveFilters['priceTo'],
  search_text: ActiveFilters['searchText'],
  free_shipping: ActiveFilters['freeShipping'],
  is_on_sale: ActiveFilters['isOnSale'],
  page?: string
  saved_search_id?: string
  last_viewed_product_id?: string
};

type FilterSearchParamsKeys = keyof FiltersSearchParams;

export type FilterKeys = keyof ActiveFilters;
type UseProductFiltersArgs = {
  catalogSlug?: string | null,
  dynamicCollectionSlug?: string | null,
  skip?: boolean | null
};

const DEFAULT_FETCHED_BRANDS = 6;
export const MAX_FETCHED_BRANDS = 40;

// Type names
const BRAND_TYPE: BrandFilterDataFragment['__typename'] = 'BrandType';

function toArray<T>(value: T | T[] | undefined): T[] {
  if (value === undefined) {
    return [];
  }

  if (!Array.isArray(value)) {
    return [value];
  }

  return value;
}

const convertSearchParamsToFilters = (s: FiltersSearchParams): ActiveFilters => ({
  priceFrom: s.price_from ? +s.price_from : undefined,
  priceTo: s.price_to ? +s.price_to : undefined,
  prices: toArray(s.prices).map((price) => +price),
  catalogSlug: s[FILTER_QUERIES.CATALOG],
  orderBy: s[FILTER_QUERIES.SORT] ? s[FILTER_QUERIES.SORT] : undefined,
  sizes: toArray(s.sizes).map((size) => +size),
  conditions: toArray(s.conditions).map((condition) => +condition),
  characteristics: toArray(s.characteristics).map((characteristic) => +characteristic),
  brands: toArray(s.brands).map((brand) => +brand),
  colors: toArray(s.colors).map((color) => +color),
  ukrainian: s.ukrainian,
  searchText: s.search_text,
  freeShipping: s.free_shipping ? Boolean(s.free_shipping) : undefined,
  isOnSale: s.is_on_sale ? Boolean(s.is_on_sale) : undefined,
  cities: toArray(s.cities).map((city) => +city),
});

export const convertFiltersToSearchParams = (filters: Partial<ActiveFilters>) => (
  Object.entries(filters).reduce(
    (acc, [key, value]) => {
      if (key === 'orderBy') {
        acc.sort = value as MyClothesSortTypes;
        return acc;
      }
      if (key === 'catalog') {
        acc.catalog = value as string;
        return acc;
      }
      const searchParamName = snakeCase(key) as FilterSearchParamsKeys;
      acc[searchParamName] = value as never;
      return acc;
    },
    {} as Partial<FiltersSearchParams>,
  )
);

export const useIndexedFilters = () => {
  const history = useHistory();
  const catalogRouteMatch = useCatalogRouteMatch();
  const match = useRouteMatch<{ base: string, indexedFilter: string }>(indexedFilter);

  const indexedFilterParams = useMemo(
    () => (
      match?.params?.indexedFilter
        ? queryString.parse(match.params.indexedFilter) as unknown as FiltersSearchParams
        : {} as FiltersSearchParams
    ),
    [match],
  );

  const activeFilters = useMemo(
    () => Object.fromEntries(
      Object.entries(convertSearchParamsToFilters(indexedFilterParams)).filter(
        ([, value]) => (Array.isArray(value) ? !!value.length : !!value),
      ),
    ) as Partial<ActiveFilters>,
    [indexedFilterParams],
  );

  const hasFilters = useMemo(
    () => !!Object.keys(activeFilters).length,
    [activeFilters],
  );

  const generateIndexedFilterPath = useCallback(
    (params: Partial<ActiveFilters>) => {
      const convertedParams = convertFiltersToSearchParams(params);

      if (catalogRouteMatch) {
        return decodeURIComponent(
          generatePath(
            indexedFilter,
            {
              base: 'slug' in catalogRouteMatch.params
                ? `${catalogRouteMatch.params.namespace}/${catalogRouteMatch.params.slug}`
                : catalogRouteMatch.params.namespace,
              indexedFilter: queryString.stringify(convertedParams),
            },
          ),
        );
      }

      return history.location.pathname;
    },
    [catalogRouteMatch, history.location.pathname],
  );

  const clearAllFilters = useCallback(
    (replaceHistory?: boolean) => {
      if (match) {
        if (replaceHistory) {
          history.replace({ ...history.location, pathname: `/${match.params.base}` });
        } else {
          history.push({ ...history.location, pathname: `/${match.params.base}` });
        }
      }
    },
    [history, match],
  );

  // Useful for queries
  const indexedFiltersInput = hasFilters ? {
    characteristicChoiceId: activeFilters?.characteristics?.length
      ? `${activeFilters.characteristics[0]}`
      : undefined,
    color: activeFilters?.colors?.length
      ? `${activeFilters.colors[0]}`
      : undefined,
    brandId: activeFilters?.brands?.length
      ? `${activeFilters.brands[0]}`
      : undefined,
  } : undefined;

  // Same as `indexedFiltersInput` but with int filters. Will be deprecated after
  // moving to api v5
  const indexedFiltersInputLegacy = hasFilters ? {
    characteristicChoiceId: activeFilters?.characteristics?.length
      ? activeFilters.characteristics[0]
      : undefined,
    color: activeFilters?.colors?.length
      ? activeFilters.colors[0]
      : undefined,
    brandId: activeFilters?.brands?.length
      ? activeFilters.brands[0]
      : undefined,
  } : undefined;

  return {
    basePath: match?.params.base,
    activeFilters,
    hasFilters,
    generateIndexedFilterPath,
    clearAllFilters,
    indexedFiltersInput,
    indexedFiltersInputLegacy,
  };
};

export const useFilterData = () => {
  const {
    activeFilters: activeIndexedFilters,
    clearAllFilters: clearIndexedFilters,
  } = useIndexedFilters();
  const [s, replaceParams, setParams] = useSearchParams<FiltersSearchParams>();

  const filters = useMemo(
    () => ({ ...convertSearchParamsToFilters(s as FiltersSearchParams), ...activeIndexedFilters }),
    [s, activeIndexedFilters],
  );

  const activeFilters: Partial<ActiveFilters> = useMemo(
    () => {
      const activeFiltersEntries = Object.entries(filters)
        .filter(
          ([key, value]) => {
            if (key === 'orderBy') {
              return false;
            }

            if (Array.isArray(value)) {
              return value.length > 0;
            }
            return !!value;
          },
        );

      return Object.fromEntries(activeFiltersEntries);
    },
    [filters],
  );

  const activeFiltersCount = useMemo(() => Object.keys(activeFilters).length, [activeFilters]);

  const hasFilters = useMemo(() => !!activeFiltersCount, [activeFiltersCount]);

  const setFilter = useMemo(
    () => (params: Partial<ActiveFilters>, replaceHistory?: boolean) => {
      const replacedParams = convertFiltersToSearchParams({
        ...activeIndexedFilters,
        ...params,
      });
      replacedParams.saved_search_id = undefined;
      replacedParams.last_viewed_product_id = undefined;
      replacedParams.page = undefined;

      replaceParams(
        replacedParams,
        replaceHistory,
      );
      clearIndexedFilters(true);
    },
    [replaceParams, clearIndexedFilters, activeIndexedFilters],
  );

  const clearAllFilters = useCallback(
    () => {
      setParams({});
      clearIndexedFilters(true);
    },
    [setParams, clearIndexedFilters],
  );

  return {
    filters,
    activeFilters,
    activeFiltersCount,
    hasFilters,
    setFilter,
    clearAllFilters,
  };
};

type Filters = Partial<ProductFeedFiltersQuery> & {
  filterCharacteristic?: NonNullable<ProductCharacteristicsFilterQuery['catalog']>['characteristics'];
  filterCities?: NonNullable<FeedCityQuery['city']>[]
};

export const useProductFilters = ({
  catalogSlug,
  skip,
}: UseProductFiltersArgs) => {
  const client = useApolloClient();
  const {
    filters: rawFilters,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    activeFilters: onlyActiveFilters,
    ...rest
  } = useFilterData();
  const initialActiveBrandsRef = useRef(rawFilters.brands);

  const { data: productFeedFiltersData } = useProductFeedFiltersQuery({
    context: {
      v5: true,
      batch: true,
    },
    variables: {
      catalogSlug,
      limitBrands: initialActiveBrandsRef.current.length || DEFAULT_FETCHED_BRANDS,
      brandIds: initialActiveBrandsRef.current.length ? initialActiveBrandsRef.current : null,
    },
    skip: !!skip,
    onCompleted: (data) => {
       
      setFilters((prev) => ({
        ...prev,
        ...data,
      }));
    },
  });
  const { data: productCharacteristicsFilterData } = useProductCharacteristicsFilterQuery({
    context: { batch: true },
    variables: {
      catalogSlug,
    },
    skip: !!skip || !catalogSlug,
    onCompleted: (data) => {
       
      setFilters((prev) => ({
        ...prev,
        filterCharacteristic: data.catalog?.characteristics || [],
      }));
    },
  });
  const { data: citiesFilterData } = useFeedCityQuery({
    context: { batch: true },
    variables: {
      id: rawFilters.cities[0],
    },
    skip: !!skip || !rawFilters.cities.length,
    onCompleted: (data) => {
       
      setFilters((prev) => ({
        ...prev,
        filterCities: data?.city ? [data.city] : [],
      }));
    },
  });

  // We use additional state here to show previous data when
  // new data loading. Without it when data is loading - state is empty
  // and filters content will disappear
  const [filters, setFilters] = useState<Filters>(() => ({
    ...(productFeedFiltersData || {}),
    ...(productCharacteristicsFilterData?.catalog?.characteristics
      ? { filterCharacteristic: productCharacteristicsFilterData.catalog.characteristics }
      : {}),
    ...(citiesFilterData ? { filterCities: [citiesFilterData.city] } : {}),
  }));

  const activeFilters = {
    ...rawFilters,
    prices: filters?.filterPrices?.length
      ? filters.filterPrices.filter((price) => rawFilters.prices.includes(+price.id))
      : [],
    sizes: filters?.filterSizes?.length
      ? filters.filterSizes.filter((size) => rawFilters.sizes.includes(+size.id))
      : [],
    conditions: filters?.filterConditions?.length
      ? filters.filterConditions.filter(
        (condition) => rawFilters.conditions.includes(+condition.id),
      )
      : [],
    brands: !filters?.filterBrands?.length ? [] : rawFilters.brands.map(
      (brandId) => client.readFragment<BrandFilterDataFragment>({
        id: `${BRAND_TYPE}:${brandId}`,
        fragment: BrandFilterDataFragmentDoc,
      })!,
    ).filter(v => !!v).reverse(),
    colors: filters?.filterColors?.length
      ? filters.filterColors.filter((color) => rawFilters.colors.includes(+color.id))
      : [],
    cities: filters?.filterCities?.length
      ? filters.filterCities.filter((city) => rawFilters.cities.includes(city.id))
      : [],
    characteristics: filters?.filterCharacteristic?.length
      ? filters.filterCharacteristic.reduce<NonNullable<Filters['filterCharacteristic']>[0]['choices']>(
        (acc, characteristic) => {
          characteristic.choices.forEach((choice) => {
            if (rawFilters.characteristics.includes(choice.id)) {
              acc.push(choice);
            }
          });
          return acc;
        },
        [],
      )
      : [],
  };

  return {
    filters: filters ? {
      ...filters,
      filterBrand: [
        ...activeFilters.brands,
        ...(
          filters.filterBrands?.filter(
            (brand) => !activeFilters.brands.some((activeBrand) => activeBrand.id === brand.id),
          ) || []
        ),
      ],
    } : undefined,
    activeFilters,
    rawActiveFilters: rawFilters,
    loading: !Object.keys(filters).length,
    ...rest,
  };
};

export const useWithIndexedFilters = (): boolean => {
  const isClient = useIsClient();
  const catalogRouteMatch = useCatalogRouteMatch();
  const { hasFilters } = useFilterData();
  const isSPAPage = !isClient || window.BASE_TEMPLATE_SOURCE === 'ssr';

  return (
    isSPAPage
    && !!catalogRouteMatch
    && !('city' in catalogRouteMatch.params)
    && !hasFilters
  );
};
